import { ProjectTerminologyNullableType } from 'src/models/Campaigns/misc';
import { PaymentMethodType } from 'src/models/paymentMethod';

type PaymentMethodModelType = PaymentMethodType & {
  IsSavedUserPaymentMethod: boolean;
};

export enum ParticipantTypeCode {
  Member = 1,
  Team = 2,
  Organization = 3,
}

export type CheckoutConfirmationResponseType = {
  Header: string;
  PaymentSummaryTitle: string;
  HeadlinesSubsection: {
    Headlines: string[];
    ProcessedTimeLabel: string;
    ContributionID: number;
    PaymentID: number;
    ShowPaymentIDInsteadOfContributionID: boolean;
    PureContributionTerminology: ProjectTerminologyNullableType;
    DisplayLicenseNumber: boolean;
    LicenseNumber: string;
    IsVisible: boolean;
  };
  SharingSubsection: {
    SharingLink: string;
    IsVisible: boolean;
    MobileSharingTitle: string;
    MobileSharingText: string;
    EmailSubject: string;
    EmailBody: string;
    CampaignSharingImprovements26794Enabled: boolean;
  };
  FundraisingPageSubsection: {
    SelectedTeamRegistrations: { Name: string; FundraisingPageUrl: string }[];
    SelectedTeamMemberRegistrations: { Name: string; FundraisingPageUrl: string }[];
    HasTeamRegistrations: boolean;
    HasTeamMemberRegistrations: boolean;
    PureContributionTerminology: ProjectTerminologyNullableType;
    TeamTerminology: ProjectTerminologyNullableType;
    TeamMemberTerminology: ProjectTerminologyNullableType;
    SupporterTerminology: ProjectTerminologyNullableType;
    DonationTerminology: ProjectTerminologyNullableType;
    IsCampaignPledge: boolean;
    IsVisible: boolean;
    IsFundraisingPagesEnabled: boolean;
  };
  ThankYouSubsection: {
    Text: string;
    IsVisible: boolean;
  };
  CheckInInstructionsSubsection: {
    Text: string;
    IsVisible: boolean;
  };
  QrCodeCheckInSubsection: {
    QrCodeSvg: string;
    IsVisible: boolean;
  };
  CheckInformationSubsection: {
    CheckPayableTo: string | null;
    CheckName: string | null;
    CheckAttentionTo: string | null;
    CheckAddressLine1: string | null;
    CheckAddressLine2: string | null;
    CheckAddressLine3: string | null;
    CheckRegion: string | null;
    CheckCountry: string | null;
    CheckCity: string | null;
    CheckZipCode: string | null;
    UnpaidCheckNotice: string | null;
    IsCampaignPledge: boolean;
    ProjectTerminology: ProjectTerminologyNullableType;
    ContributionTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
  };
  RegistrationLinesSubsection: {
    RegistrationTeamMembersLines: {
      Name: string;
      TeamName: string;
      RegistrationTitle: string;
      RegistrationPrice: number;
    }[];
    RegistrationTeamsLines: {
      Name: string;
      RegistrationTitle: string;
      RegistrationPrice: number;
      InvitedMembers: string;
      Goal: number | null;
    }[];
    ShowTeamColumn: boolean;
    AreTeamsEnabled: boolean;
    CurrencyType: number;
    ShowTeamGoalColumn: boolean;
    ShowTeamInvitesColumn: boolean;
    TeamTerminology: ProjectTerminologyNullableType;
    TeamMemberTerminology: ProjectTerminologyNullableType;
    ContributionTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
  };
  GiftAidSubsection: {
    IsVisible: boolean;
    ContributionTerminology: ProjectTerminologyNullableType;
    OrganizationCommonName: string | null;
    GiftAidTitle: string | null;
    GiftAidFirstName: string | null;
    GiftAidLastName: string | null;
    GiftAidAddressLine1: string | null;
    GiftAidAddressLine2: string | null;
    GiftAidAddressLine3: string | null;
    GiftAidCity: string | null;
    GiftAidPostalCode: string | null;
  };
  OrganizationTaxNoticeSubsection: {
    IsVisible: boolean;
    TaxNotice: string;
  };
  DeliverySubsection: {
    IsVisible: boolean;
    ShippingFirstName: string | null;
    ShippingLastName: string | null;
    ShippingPhone: string | null;
    IsShippingPhoneEmpty: boolean;
    IsShippingAddressEmpty: boolean;
    ShowDeliveryInstructions: boolean;
    ShippingCountryID: string | null;
    ShippingOrganization: string | null;
    ShippingAddressLine1: string | null;
    ShippingAddressLine2: string | null;
    ShippingAddressLine3: string | null;
    ShippingPostalCode: string | null;
    ShippingCountry: string | null;
    ShippingCity: string | null;
    ShippingRegionName: string | null;
    DeliveryInstructions: string | null;
    PrizeTerminologyModel: ProjectTerminologyNullableType;
    DeliveryTypeCode: number | null;
    TerminologySingular: string | null;
  };
  ButtonsSubsection: {
    IsVisible: boolean;
    Buttons: {
      Title: string;
      BackgroundColor: string;
      TextColor: string;
      IsDisabled: boolean;
      Url: string;
      IsVisible: boolean;
    }[];
    CanEdit: boolean;
  };
  ViralSharingSubsection: {
    IsVisible: boolean;
    Terminology: ProjectTerminologyNullableType;
    BonusType: number;
    BonusQuantityForSocialMedia: string | null;
    BonusAmountForSharer: number | null;
    BonusAmountForPurchaser: number | null;
    FreeEntriesHeadline: string | null;
    CampaignUrl: string | null;
    PromoterReferralCode: string | null;
    DisablePromoterButton: boolean;
    AddToPromotersUrl: string;
    LoggedAsPromoter: boolean;
    PromoterUrl: string | null;
    BonusQuantityForSocialMediaString: string | null;
    BonusAmountForSharerString: string;
    BonusAmountForPurchaserString: string;
    AvailableBonusesCount: number;
  };
  SaleItemsSubSection: {
    SectionHeader: string;
    Items: {
      AttributesSets: {
        Attributes: {
          Name: string;
          Value: string;
        }[];
        Quantity: number;
      }[];
      Quantity: number;
      Amount: number;
      ItemTitle: string;
    }[];
    CurrencyType: number;
    ItemsTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
  };
  RaffleTicketsSubSection: {
    SectionHeader: string;
    Items: {
      PrizeTitle: string;
      TicketNumbers: string;
      Quantity: number;
      Amount: number;
      ItemTitle: string;
    }[];
    ShowPrizeColumn: boolean;
    CurrencyType: number;
    PrizeTerminology: ProjectTerminologyNullableType;
    TicketsTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
  };
  SweepstakesTicketsSubSection: {
    SectionHeader: string;
    Items: {
      PrizeTitle: string;
      TicketNumbers: string;
      Quantity: number;
      Amount: number;
      ItemTitle: string;
    }[];
    CurrencyType: number;
    PrizeTerminology: ProjectTerminologyNullableType;
    TicketsTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
  };
  EventRegistrationsSubSection: {
    TicketsTerminology: ProjectTerminologyNullableType;
    SectionHeader: string;
    Items: {
      FirstAndLastName: string;
      CustomFields: {
        Name: string;
        Value: string;
        Sequence: number;
      }[];
      Quantity: number;
      Amount: number;
      ItemTitle: string;
      CampaignRegistrationId: number;
    }[];
    CurrencyType: number;
    IsVisible: boolean;
  };
  TicketingGroupRegistrationsSubsection: {
    TicketGroupsTerminology: ProjectTerminologyNullableType;
    SectionHeader: string;
    Items: {
      GroupName: string;
      CustomFields: {
        Name: string;
        Value: string;
        Sequence: number;
      }[];
      Quantity: number;
      Amount: number;
      ItemTitle: string;
      CampaignGroupRegistrationId: number;
    }[];
    CurrencyType: number;
    IsVisible: boolean;
  };
  TicketingGroupTicketRegistrationsSubsection: {
    TicketGroupsTerminology: ProjectTerminologyNullableType;
    TicketsTerminology: ProjectTerminologyNullableType;
    SectionHeader: string;
    Items: {
      GroupTitle: string;
      GroupName: string;
      FirstAndLastName: string;
      InvitationLink: string;
      CustomFields: {
        Name: string;
        Value: string;
        Sequence: number;
      }[];
      Quantity: number;
      Amount: number;
      ItemTitle: string;
      CampaignRegistrationId: number;
    }[];
    CurrencyType: number;
    IsVisible: boolean;
  };
  CrowdfundingRewardsSubSection: {
    SectionHeader: string;
    Items: {
      ItemTitle: string;
      Quantity: number;
      Amount: number;
    }[];
    CurrencyType: number;
    ItemsTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
  };
  TeamMemberPaymentsSubSection: {
    SectionHeader: string;
    Items: {
      MaximumAmount: number;
      UnitTerminology: ProjectTerminologyNullableType;
      ID: number;
      TeamID: number;
      TeamName: string;
      TeamMemberID: number;
      TeamMemberName: string;
      TeamMemberFirstName: string;
      AmountPerUnit: number;
      AmountPercentage: number;
      IsAmountPercentageManual: boolean;
      Amount: number;
      ActivityPayments: {
        AthonActivityID: number;
        ContributionAmountPresetID: number;
        Amount: number;
        MaximumAmount: number;
        AmountPerUnit: number;
        GoalUnits: number;
        GoalAmount: number;
        ActivityTitle: string;
        ContributionAmountPresets: {
          ID: number;
          Amount: number;
          CurrencyType: number;
          Title: string;
        }[];
        UnitTerminologySingular: string;
        UnitTerminologyPlural: string;
        UsedAmountType: string;
        UsedAmountPerUnit: number;
      }[];
      TeamMemberWriteInCredit: string;
      TeamWriteInCredit: string;
      IsTeamMemberNotFound: boolean;
      IsTeamNotFound: boolean;
      IsMemberPayment: boolean;
      IsTeamPayment: boolean;
    }[];
    TeamTerminology: ProjectTerminologyNullableType;
    TeamMemberTerminology: ProjectTerminologyNullableType;
    DonationTerminology: ProjectTerminologyNullableType;
    CurrencyType: number;
    IsPledge: boolean;
    IsDueLater: boolean;
    PaymentID: null;
    IsVisible: boolean;
    AvailableActivities: string[];
    HasNotAvailableCreditActivities: boolean;
    ParticipantTypeID: ParticipantTypeCode;
  };
  AuctionBidsSubSection: {
    SectionHeader: string;
    Items: {
      DisplayStatusID: number;
      StatusID: number;
      AuctionItemName: string;
      MaximumBidAmount: number;
      MaximumBidAmountString: string;
      CurrentBidAmount: number;
      CurrentBidAmountString: string;
      FairMarketValueString: string;
    }[];
    ItemsTerminology: ProjectTerminologyNullableType;
    ShowMaximumBidColumn: boolean;
    ShowFairMarketValueColumn: boolean;
    IsVisible: boolean;
  };
  AuctionBuyItNowItemsSubSection: {
    SectionHeader: string;
    Items: {
      Quantity: number;
      Amount: number;
      ItemTitle: string;
      FairMarketValueString: string;
    }[];
    CurrencyType: number;
    ItemsTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
    ShowFairMarketValueColumn: boolean;
  };
  AuctionGiftSubSection: {
    SectionHeader: string;
    Items: {
      GiftAmount: string;
      TipsOrProcessingFeeAmount: string;
      PaymentStatusCode: number;
      HasTips: boolean;
      HasFees: boolean;
    }[];
    IsVisible: boolean;
  };
  ShowItemsSummarySection: boolean;
  ChargedNowPaymentSubsection: {
    PaymentTerm: string;
    Lines: {
      Title: string;
      Value: string;
    }[];
    IsVisible: boolean;
  };
  PledgePaymentSubsection: {
    IsChargedLaterLabelVisible: boolean;
    PaymentTerm: string;
    Lines: {
      Title: string;
      Value: string;
    }[];
    IsVisible: boolean;
  };
  BidPaymentSubsection: {
    IsChargedLaterLabelVisible: boolean;
    PaymentTerm: string;
    Lines: {
      Title: string;
      Value: string;
    }[];
    IsVisible: boolean;
  };
  AuctionGiftPaymentSubsection: {
    IsChargedLaterLabelVisible: boolean;
    PaymentTerm: string;
    Lines: {
      Title: string;
      Value: string;
    }[];
    IsVisible: boolean;
  };
  ShowPaymentsSummarySection: boolean;
  ShowPaymentMethodSubsection: boolean;
  PaymentMethodModel: PaymentMethodModelType;
  ShowOldPaymentMethodSubsection: boolean;
  PermanentUrl: string;
  CampaignName: string;
  ShowSummarySection: boolean;
  ContributionGuid: string;
  PaymentGuid: null;
  ContinueUrl: string;
  CanEditContribution: boolean;
  IsInTestMode: boolean;
  DonorSupportTypeID: number;
  GatedLiveStreamSubsection: {
    IsVisible: boolean;
    TicketsTerminology: ProjectTerminologyNullableType;
    StartDateTime: string;
    Items: {
      PermanentUrl: string;
      AccessCode: string;
      CheckResult: number;
      LivestreamUrl: string;
      AccessLink: string;
    }[];
  };
  ProjectTerminology: ProjectTerminologyNullableType;
  ContributionTerminology: ProjectTerminologyNullableType;
  PureContributionTerminology: ProjectTerminologyNullableType;
  TeamMemberTerminology: ProjectTerminologyNullableType;
  TeamTerminology: ProjectTerminologyNullableType;
  SupporterTerminology: ProjectTerminologyNullableType;
  RafflePrizeTerminology: ProjectTerminologyNullableType;
  Title: string;
  Content: null;
  CampaignUrl: string;
};

export type CheckoutContributionConfirmationResponseType = CheckoutConfirmationResponseType & {
  ShowChargedNowPaymentMethodSubsection: boolean;
  ShowChargedLaterPaymentMethodSubsection: boolean;
  PublicNoticeSubsection: {
    CanSaveContributionNote: boolean;
    ProjectTerminology: ProjectTerminologyNullableType;
    IsVisible: boolean;
    Note: string | null;
    ContributionGuid: string;
  };
  NotificationPreferencesSubsection: {
    ProjectTerminology: ProjectTerminologyNullableType;
    CanSubscribeToCampaignUpdates: boolean;
    ReceiveEmailCampaignUpdates: boolean;
    IsVisible: boolean;
  };
  SpecialInstructionsSubsection: {
    CanEdit: boolean;
    ProjectTerminology: ProjectTerminologyNullableType;
    Text: string | null;
    IsVisible: boolean;
  };
  ChargedNowPaymentMethodModel: PaymentMethodModelType | null;
  ChargedLaterPaymentMethodModel: PaymentMethodModelType;
  AdditionalInfoSubsection: {
    IsVisible: boolean;
    ContributionTerminology: ProjectTerminologyNullableType;
    TeamMemberTerminology: ProjectTerminologyNullableType;
    ProjectTerminology: ProjectTerminologyNullableType;
    AuctionItemsTerminology: ProjectTerminologyNullableType;
    RecurrencePattern: {
      RecurrencePeriod: number;
      RecurrenceType: number;
      TotalOccurences: number;
      EndDate: string;
      EndDateString: string;
      RecurrencePeriodLy: string;
      RecurrencePeriodPlural: string;
      RecurrencePeriodSingular: string;
    } | null;
    TotalAmountString: string;
    ManageContributionsUrl: string;
    IsDonationPage: boolean;
    HasAnyPerUnitPledge: boolean;
    HasBids: boolean;
    AuctionSmsAlertsEnabled: boolean;
    IsAuctionProcessingStarted: boolean;
    IsDedicatedDonation: boolean;
    DedicationType: string;
    DedicatedToName: string;
    DedicatedToEmail: string;
    HasCustomFields: boolean;
    CustomFields: {
      Name: string;
      Value: string;
      Sequence: number;
    }[];
  };
};

export type CheckoutSaveContributionNoteRequestType = Pick<
  CheckoutContributionConfirmationResponseType['PublicNoticeSubsection'],
  'Note' | 'ContributionGuid'
>;

export type CheckoutConfirmationSavePreferencesRequestType = {
  ContributionGuid: string;
  SpecialInstructions?: string | null;
  ReceiveEmailCampaignUpdates?: boolean;
};
